import React, { useState, useMemo, useEffect, Fragment, useRef } from 'react';
import styles from 'assets/jss/material-dashboard-pro-react/views/sweetAlertStyle';
import { wsGet, wsCreate, wsGetAll, wsRemove } from 'services/WService';
import CardContainer from 'components/Tvendo/Common/CardContainer';
import SweetAlert from 'react-bootstrap-sweetalert';
import { Toasty, useToasty } from 'hooks/Toasty';
import { makeStyles } from '@material-ui/core';
import { WithApp } from 'contexts/appContext';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment/moment';

// Components
import NewDepositCashAccount from './NewDeposit/NewDepositCashAccount';
import NewDepositHeader from './NewDeposit/NewDepositHeader';
import NewDepositChecks from './NewDeposit/NewDepositChecks';
import ImportDeposits from './NewDeposit/ImportDeposits';
import RegularButton from 'components/CustomButtons/Button';
import GridItem from 'components/Grid/GridItem';
import GridContainer from 'components/Grid/GridContainer';

const defaultDeposit = {
  DeposType: 'C',
  DeposDate: moment().format('YYYY-MM-DD'),
  TaxDate: moment().format('YYYY-MM-DD'),
  BanckAcct: '',
  DpsBank: '',
  Memo: '',
  LocTotal: 0,
  Ref2: '',
  AllocAcct: '',
  U_SYP_MPPG: '001',
};

const useStyles = makeStyles(() => ({
  ...styles,
}));

function NewDeposit() {
  const { id } = useParams();
  const classes = useStyles();
  const navigate = useNavigate();
  const [notify, showNotify] = useToasty();
  const isDisabled = useMemo(() => !(parseInt(id) === 0), [id]);
  const [deposit, setDeposit] = useState(defaultDeposit);
  const [accountSap, setAccountSap] = useState([]);
  const [depositChecks, setDepositChecks] = useState([]);
  const [originAccounts, setOriginAccounts] = useState([]);

  const inputFileRef = useRef(null);
  const inputFileRefOpe = useRef(null);
  const [importData, setImportData] = useState(undefined);
  const [importDataOperation, setImportDataOperation] = useState(undefined);
  const [selectionModel, setSelectionModel] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [groupKey, setGroupKey] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [alert, setAlert] = useState(null);
  const [mismatchIds, setMismatchIds] = useState(new Set());

  async function anulateDeposit() {
    setAlert(
      <SweetAlert
        warning
        style={{ display: 'block', marginTop: '-100px' }}
        title={`¿Está seguro de anular el depósito ${deposit.DpsBank} ${deposit.Memo}?`}
        onConfirm={async () => {
          setAlert(null);
          let res = await wsRemove('pymt/deposit', deposit.id);
          if (res.success) {
            showNotify(res.message);
            navigate('/admin/bancos/operaciones/depositos/');
          } else {
            showNotify(res.message, 'error');
          }
        }}
        onCancel={() => setAlert(null)}
        confirmBtnCssClass={classes.button + ' ' + classes.success}
        cancelBtnCssClass={classes.button + ' ' + classes.danger}
        confirmBtnText="Aceptar"
        cancelBtnText="Cancelar"
        showCancel
      >
        Esta acción no se puede revertir
      </SweetAlert>,
    );
  }

  async function process() {
    if (deposit.BanckAcct?.length !== 8) {
      showNotify('Seleccione una cuenta destino', 'info');
      return;
    }

    if (deposit.DpsBank.length === 0) {
      showNotify('Ingrese el nombre del banco', 'info');
      return;
    }

    let data = { ...deposit };
    if (deposit.DeposType === 'C') {
      if (parseFloat(data.LocTotal) === 0) {
        showNotify('Ingrese el monto del déposito', 'info');
        return;
      }
      if (data.AllocAcct?.length !== 8) {
        showNotify('Seleccione una cuenta origen', 'info');
        return;
      }
    }

    if (deposit.DeposType === 'K') {
      delete data.AllocAcct;
      delete data.LocTotal;
      if (depositChecks.length === 0) {
        showNotify('Seleccione al menos un cheque', 'info');
        return;
      }
      data.checks = depositChecks;
    }

    let res = await wsCreate('pymt/deposit', data);
    if (res.success === false) {
      showNotify(res.message, 'error');
      return;
    }

    showNotify(res.message, 'success');
    setDeposit(defaultDeposit);
    setDepositChecks([]);
  }

  function addGroup() {
    let selected = importData.filter((item) => selectionModel.includes(item.id));
    setSelectionModel([]);
    setSelectedGroups((groups) => {
      let newGroups = [...groups];
      newGroups.push({
        id: groupKey,
        items: selected,
        // Cuenta destino
        CtaDestino: deposit.CtaDestino,
        BanckAcct: deposit.BanckAcct,
        // Cuenta de origen
        CtaOrigen: deposit.CtaOrigen,
        AllocAcct: deposit.AllocAcct,
        DeposDate: deposit.DeposDate,
      });
      return newGroups;
    });
    setGroupKey((key) => key + 1);
    setImportData((importData) => {
      let newImportData = [...importData];
      newImportData = newImportData.filter((item) => !selectionModel.includes(item.id));
      return newImportData;
    });
  }

  async function importExcel(e) {
    if (e.target.files.length === 0) {
      window.alert('Seleccione un archivo excel');
      e.target.value = '';
      return;
    }

    let formData = new FormData();
    formData.append('import', e.target.files[0]);

    setProcessing(true);
    let res = await wsCreate('pymt/deposit/store-excel', formData);
    setProcessing(false);
    inputFileRef.current.value = '';
    if (res.success) {
      setImportData(res.data.map((item, index) => ({ id: index, ...item })));
    } else {
      showNotify(res.message, 'error');
    }
  }
  async function importExcelOperation(e) {
    if (e.target.files.length === 0) {
      window.alert('Seleccione un archivo excel de operaciones');
      e.target.value = '';
      return;
    }

    let formData = new FormData();
    formData.append('import', e.target.files[0]);

    setProcessing(true);
    let res = await wsCreate('pymt/deposit/store-excel-operation', formData);
    setProcessing(false);
    inputFileRef.current.value = '';
    if (res.success) {
      setImportDataOperation(res.data.map((item, index) => ({ id: index, ...item })));
      await persistImportedOperations(res.data);
      showNotify(res.message, 'success');
    } else {
      showNotify(res.message, 'error');
    }
  }

  async function persistImportedOperations(data = importDataOperation) {
    if (data.length === 0) {
      showNotify('No hay datos para guardar', 'warning');
      return;
    }

    setProcessing(true);
    const res = await wsCreate('pymt/deposit/save-operations', {
      operations: data,
    });
    setProcessing(false);

    if (res.success) {
      showNotify(res.message, 'success');
    } else {
      showNotify(res.message, 'error');
    }
  }

  useEffect(() => {
    wsGetAll('pymt/deposit/origin-accounts').then((res) => setOriginAccounts(res.data));
    if (!isDisabled) return;
    wsGet('pymt/deposit', id).then((res) => {
      if (!res.success) return;
      let { checks, ...deposit } = res.data;
      deposit.DeposDate = moment(deposit.DeposDate).format('YYYY-MM-DD');
      deposit.TaxDate = moment(deposit.TaxDate).format('YYYY-MM-DD');
      setDeposit(deposit);
      setDepositChecks(checks || []);
    });
  }, []);
  
  useEffect(() => {
    if (deposit?.BanckAcct !== '') {
      setAccountSap([]);
      wsGetAll(`pymt/deposit/accounts-sap?BanckAcct=${deposit?.BanckAcct}`).then((res) => setAccountSap(res.data));
    }
  }, [deposit]);


  function validateConciliation() {
    if (!importData || !importDataOperation) return;

    if (!deposit?.BanckAcct || deposit.BanckAcct.trim() === '') {
      showNotify('Seleccione una cuenta destino antes de validar la conciliación ❗', 'warning');
      return;
    }

    const selectedAccount = accountSap.find(acc => acc.GLAccount === deposit.BanckAcct)?.Account;
    console.log(selectedAccount);
    if (!selectedAccount && accountSap.length === 0) {
      showNotify('No se encontró la cuenta SAP asociada al código seleccionado', 'warning');
      return;
    }

    // ✅ Filtrar solo los registros de la cuenta seleccionada
    const filteredOperations = importDataOperation.filter(op => op.account === selectedAccount);

    const operationMap = new Set(
      filteredOperations.map(op =>
        `${op.TaxDate}|${op.operation}|${parseFloat(op.amount).toFixed(2)}`
      )
    );

    const mismatches = new Set();
    importData.forEach(row => {
      const key = `${row.TaxDate}|${row.Ref2}|${parseFloat(row.LocTotal).toFixed(2)}`;
      // console.log('[CONCILIACIÓN]', key);

      if (!operationMap.has(key)) {
        // console.warn(`[MISMATCH] No coincide: ${key}`);
        mismatches.add(row.id);
      } 
      // else {
      //   // console.log(`[MATCH] Coincide: ${key}`);
      // }
    });

    setMismatchIds(mismatches);
    showNotify(
      mismatches.size === 0
        ? 'Todos los registros coinciden'
        : `Se encontraron ${mismatches.size} diferencias ❗`,
      mismatches.size === 0 ? 'success' : 'warning'
    );
  }

  function emptyConciliation() {
    setImportDataOperation(undefined);
    setMismatchIds(new Set());
    if (inputFileRefOpe.current) {
      inputFileRefOpe.current.value = '';
    }
  }

  return (
    <Fragment>
      <Toasty action={notify} />
      {alert}

      <input
        type="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        style={{ display: 'none' }}
        onChange={importExcel}
        ref={inputFileRef}
      />
      <input
        type="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        style={{ display: 'none' }}
        onChange={importExcelOperation}
        ref={inputFileRefOpe}
      />

      <CardContainer title={!isDisabled ? 'Nuevo deposito' : `Deposito ${id}`}>
        <NewDepositHeader
          depositId={id}
          importData={importData}
          onImport={() => {
            inputFileRef.current.click();
          }}
          onImportOpe={() => {
            inputFileRefOpe.current.click();
          }}
          importDataOperation={importDataOperation}
          selectionModel={selectionModel}
          onAddGroup={addGroup}
          deposit={deposit}
          accountSap={accountSap}
          validateOperation={validateConciliation}
          emptyConciliation={emptyConciliation}
          originAccounts={originAccounts}
          setDeposit={setDeposit}
          isDisabled={isDisabled}
          process={process}
          processing={processing}
        />
      </CardContainer>

      {importData === undefined && (
        <>
          {deposit.DeposType === 'K' && (
            <NewDepositChecks
              deposit={deposit}
              setDeposit={setDeposit}
              isDisabled={isDisabled}
              depositChecks={depositChecks}
              setDepositChecks={setDepositChecks}
            />
          )}

          {deposit.DeposType === 'C' && (
            <NewDepositCashAccount
              deposit={deposit}
              originAccounts={originAccounts}
              setOriginAccounts={setOriginAccounts}
              setDeposit={setDeposit}
              isDisabled={isDisabled}
            />
          )}
        </>
      )}

      {importData !== undefined && (
        <ImportDeposits
          importData={importData}
          setImportData={setImportData}
          selectionModel={selectionModel}
          setSelectionModel={setSelectionModel}
          groups={selectedGroups}
          setGroups={setSelectedGroups}
          mismatchIds={mismatchIds}
        />
      )}

      {id !== '0' && (
        <GridContainer>
          <GridItem xs={12} sm={6} md={8} lg={10}></GridItem>
          <GridItem xs={12} sm={6} md={4} lg={2}>
            <RegularButton
              fullWidth
              color="danger"
              onClick={anulateDeposit}
              disabled={deposit.sapAnnuled !== null}
            >
              Anular
            </RegularButton>
          </GridItem>
        </GridContainer>
      )}
    </Fragment>
  );
}

export default WithApp(NewDeposit);
